import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { CAD_APPLY_FIELD_GROUPS } from '../../hooks/useCadApplyPlan';
import {
  CadSettings as CadSettingsType,
  configurationService,
} from '../../services/configurationService';
import { ApiError } from '../../services/apiClient';
import { CadNameMatching } from './CadNameMatching';

/** Comma-separated configuration list to its trimmed members. Matches how the server reads them. */
const toList = (value: string): string[] =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

/**
 * The server matches category names through ScoreCategoryName.Canonical, so "CaseEntry",
 * "Case Entry" and "caseentry" are one category. Comparing exactly here would flag a category that
 * is configured and scoring correctly as missing from the rubric, and the warning tells the admin
 * to remove it.
 */
const canonicalCategory = (name: string): string =>
  name.trim().replace(/ /g, '').toLowerCase();

const sentence = (items: string[]): string =>
  items.length <= 1
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

export const CadSettings: React.FC = () => {
  const [settings, setSettings] = useState<CadSettingsType | null>(null);
  // What the server last gave us, so Save can send only the keys that actually changed.
  const [baseline, setBaseline] = useState<CadSettingsType | null>(null);
  const [rubricCategories, setRubricCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // No setLoading(true) here: `loading` starts true, and raising it synchronously would be a
  // setState in the effect body below. Reset raises it itself, which is the only other caller.
  const load = useCallback(async () => {
    try {
      const [loaded, categories] = await Promise.all([
        configurationService.getCadSettings(),
        configurationService.getCadRubricCategories(),
      ]);
      setSettings(loaded);
      setBaseline(loaded);
      setRubricCategories(categories);
      setError('');
    } catch (err) {
      setError((err as ApiError)?.message || 'Failed to load CAD record settings.');
    } finally {
      setLoading(false);
      setReloading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Deliberately does not raise `loading`: that unmounts the card, and with it the Reset button the
  // user just pressed, dropping focus to the document body on a very long form.
  const handleReset = useCallback(() => {
    setReloading(true);
    setSuccess('');
    void load();
  }, [load]);

  // Whole-group membership. `some` would render the operator group checked when only OperatorId is
  // stored, which is exactly the half-configured state the grouping exists to prevent: a call taker
  // assigned by id with no name, shown to the admin as correct.
  const selectedFieldGroups = useMemo(() => {
    const allowed = new Set(settings ? toList(settings.applyFields) : []);
    return new Set(
      CAD_APPLY_FIELD_GROUPS.filter((group) =>
        group.fields.every((field) => allowed.has(field)),
      ).map((group) => group.id),
    );
  }, [settings]);

  /** Stored with some of the group's fields but not all. Shown indeterminate, never as checked. */
  const partialFieldGroups = useMemo(() => {
    const allowed = new Set(settings ? toList(settings.applyFields) : []);
    return new Set(
      CAD_APPLY_FIELD_GROUPS.filter(
        (group) =>
          group.fields.some((field) => allowed.has(field)) &&
          !group.fields.every((field) => allowed.has(field)),
      ).map((group) => group.id),
    );
  }, [settings]);

  const selectedCategories = useMemo(
    () => (settings ? toList(settings.rescoreCategories) : []),
    [settings],
  );

  // A category the agency chose that the rubric no longer defines still scores nothing, silently.
  // Editing the analysis prompt does not re-validate this key, so the screen has to say it.
  const staleCategories = useMemo(() => {
    const defined = new Set(rubricCategories.map(canonicalCategory));
    return selectedCategories.filter((name) => !defined.has(canonicalCategory(name)));
  }, [selectedCategories, rubricCategories]);

  const set = useCallback(<K extends keyof CadSettingsType>(key: K, value: CadSettingsType[K]) => {
    setSettings((current) => (current ? { ...current, [key]: value } : current));
    setSuccess('');
  }, []);

  const toggleFieldGroup = useCallback((groupId: string, checked: boolean) => {
    setSettings((current) => {
      if (!current) return current;
      const group = CAD_APPLY_FIELD_GROUPS.find((g) => g.id === groupId);
      if (!group) return current;
      const allowed = new Set(toList(current.applyFields));
      group.fields.forEach((field) => (checked ? allowed.add(field) : allowed.delete(field)));
      // Written back in the canonical order rather than the order they were clicked.
      const ordered = CAD_APPLY_FIELD_GROUPS.flatMap((g) => g.fields).filter((f) => allowed.has(f));
      return { ...current, applyFields: ordered.join(',') };
    });
    setSuccess('');
  }, []);

  const handleSave = async () => {
    if (!settings) return;
    try {
      setSaving(true);
      await configurationService.updateCadSettings(settings, baseline ?? undefined);
      setBaseline(settings);
      setSuccess('CAD record settings saved.');
      setError('');
    } catch (err) {
      setError((err as ApiError)?.message || 'Failed to save CAD record settings.');
      setSuccess('');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress aria-label="Loading CAD record settings" />
      </Box>
    );
  }

  if (!settings) {
    return <Alert severity="error">{error || 'CAD record settings could not be loaded.'}</Alert>;
  }

  const scoringSentence = selectedCategories.length
    ? `The CAD record will score ${sentence(
        selectedCategories,
      )} on its own, with nobody confirming the new score.`
    : 'No categories are listed, so the CAD record will score nothing even while this is on.';

  return (
    <Box>
      <Typography variant="h6" component="h2" gutterBottom>
        CAD Records
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>These settings apply to every CAD record for this agency</AlertTitle>
        They govern the document whichever way it arrives: attached by a supervisor on the call
        page, attached through the assistant, or matched to its call automatically from a bulk
        upload. There is no separate set of rules per source.
      </Alert>

      {/* Assertive, not polite: a refused save is the one thing that must interrupt. Without this a
          400 from the bottom of a long form is silence for a screen reader and an off-screen red box
          for everyone else, and the admin leaves believing the setting took. */}
      <Box role="alert">
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
      </Box>

      <Box role="status" aria-live="polite">
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
      </Box>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={3}>
            {/* ---- The feature itself ---- */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enabled}
                    onChange={(e) => set('enabled', e.target.checked)}
                    inputProps={{ 'aria-describedby': 'cad-enabled-help' }}
                  />
                }
                label="Allow CAD event reports to be attached to calls"
              />
              <Typography id="cad-enabled-help" variant="body2" color="text.secondary">
                While this is off, the CAD Record tab does not appear and no CAD report can be
                attached, read or deleted. Turning it on stores a second copy of a criminal justice
                record, the CAD report, alongside the recording.
              </Typography>
            </Box>

            {/* ---- Bulk upload ---- */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.bulkUploadEnabled}
                    disabled={!settings.enabled}
                    onChange={(e) => set('bulkUploadEnabled', e.target.checked)}
                    inputProps={{ 'aria-describedby': 'cad-bulk-help' }}
                  />
                }
                label="Accept CAD reports in bulk uploads and match them to calls automatically"
              />
              <Typography id="cad-bulk-help" variant="body2" color="text.secondary">
                The Upload page then accepts CAD report PDFs with the audio files. A report is
                matched to the call whose incident number is in its file name or printed in the
                report, and only when exactly one call matches and that call has no CAD report yet.
                A report no call matches waits on the dashboard, where a supervisor can attach it
                from the call. A matched report follows every setting below, including filling the
                call record when that is on.
                {!settings.enabled && ' Turn on CAD event reports above to use this.'}
              </Typography>
            </Box>

            <TextField
              label="Days to keep a CAD report no call matches"
              type="number"
              value={settings.inboxRetentionDays}
              onChange={(e) => set('inboxRetentionDays', e.target.value)}
              disabled={!settings.enabled}
              slotProps={{ htmlInput: { min: 1, max: 365, step: 1 } }}
              sx={{ maxWidth: 360 }}
              helperText="1 to 365. A waiting report is a stored copy of a criminal justice record with no call to anchor it, so it is deleted after this many days."
            />

            <CadNameMatching
              audioPattern={settings.audioNameRegex}
              reportPattern={settings.reportNameRegex}
              onAudioPattern={(pattern) => set('audioNameRegex', pattern)}
              onReportPattern={(pattern) => set('reportNameRegex', pattern)}
            />

            <TextField
              label="Notes on this agency's CAD export"
              value={settings.readingNotes}
              onChange={(e) => set('readingNotes', e.target.value)}
              multiline
              minRows={2}
              helperText="Field labels, time zone and unit naming particular to your CAD system. Added to the instructions the model reads the PDF with. Leave empty until your first export has been reviewed. Describe the layout only: do not paste a real call's details here, because every user in the agency can read this value."
            />

            <Divider />

            {/* ---- The check against the recording ---- */}
            <Typography variant="subtitle1" component="h3">
              Checking the CAD entry against the recording
            </Typography>

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.autoVerify}
                    onChange={(e) => set('autoVerify', e.target.checked)}
                    inputProps={{ 'aria-describedby': 'cad-verify-help' }}
                  />
                }
                label="Check every CAD record against the call automatically"
              />
              <Typography id="cad-verify-help" variant="body2" color="text.secondary">
                The check writes nothing to the call record and discloses nothing new. With this
                off, a supervisor runs it from the CAD Record tab.
              </Typography>
            </Box>

            <TextField
              label="Check instructions"
              value={settings.verificationPrompt}
              onChange={(e) => set('verificationPrompt', e.target.value)}
              multiline
              minRows={4}
              helperText="What your agency wants checked against the call. Leave empty to use the platform default. The format of the answer is handled automatically and cannot be changed here."
            />

            <Divider />

            {/* ---- Filling the call record ---- */}
            <Typography variant="subtitle1" component="h3">
              Filling the call record from the CAD record
            </Typography>

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.autoApply}
                    onChange={(e) => set('autoApply', e.target.checked)}
                    inputProps={{ 'aria-describedby': 'cad-apply-help' }}
                  />
                }
                label="Fill the call record automatically"
              />
              <Typography id="cad-apply-help" variant="body2" color="text.secondary">
                With this on, the fields below are written with nobody confirming them. That
                includes caller details and, when the operator is allowed, which call taker the call
                is attributed to, which decides who can see the recording. It applies to reports
                matched automatically from a bulk upload as much as to ones a supervisor attached.
              </Typography>
            </Box>

            <FormControl sx={{ maxWidth: 360 }}>
              <InputLabel id="cad-apply-policy-label">Which values are used</InputLabel>
              <Select
                labelId="cad-apply-policy-label"
                label="Which values are used"
                value={settings.applyPolicy}
                onChange={(e) =>
                  set('applyPolicy', e.target.value as CadSettingsType['applyPolicy'])
                }
                inputProps={{ 'aria-describedby': 'cad-apply-policy-help' }}
              >
                <MenuItem value="FillEmpty">Fill empty fields only</MenuItem>
                <MenuItem value="Overwrite">Overwrite existing values</MenuItem>
              </Select>
              <FormHelperText id="cad-apply-policy-help">
                Overwrite replaces what the call record already holds, including values a supervisor
                entered by hand. It applies to a supervisor&apos;s manual apply too, where it decides
                what is pre-selected in the dialog.
              </FormHelperText>
            </FormControl>

            {/* Below the Select, not above it: appearing above would push the control the admin just
                operated down the page. Polite live region so the escalation is announced at all. */}
            <Box role="status" aria-live="polite">
              {settings.autoApply && settings.applyPolicy === 'Overwrite' && (
                <Alert severity="warning">
                  <AlertTitle>Existing call record values will be replaced</AlertTitle>
                  Overwrite plus automatic filling means every allowed field takes the CAD value even
                  where the call record already holds something, with nobody confirming it. Choose
                  &quot;Fill empty fields only&quot; to leave existing values alone.
                </Alert>
              )}
            </Box>

            <FormControl component="fieldset" variant="standard">
              <FormLabel component="legend">Call record fields a CAD record may fill</FormLabel>
              <FormGroup>
                {CAD_APPLY_FIELD_GROUPS.map((group) => (
                  <FormControlLabel
                    key={group.id}
                    control={
                      <Checkbox
                        checked={selectedFieldGroups.has(group.id)}
                        indeterminate={partialFieldGroups.has(group.id)}
                        onChange={(e) =>
                          toggleFieldGroup(
                            group.id,
                            // Completing a half-stored group is the only sensible next step, so an
                            // indeterminate box always resolves to checked rather than toggling off.
                            partialFieldGroups.has(group.id) ? true : e.target.checked,
                          )
                        }
                        inputProps={{ 'aria-describedby': 'cad-fields-help' }}
                      />
                    }
                    label={
                      group.id === 'operator'
                        ? 'Operator (assigns the call taker, and with it who can see the recording)'
                        : group.label
                    }
                  />
                ))}
              </FormGroup>
              <Typography id="cad-fields-help" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {selectedFieldGroups.size === 0 && partialFieldGroups.size === 0
                  ? 'No fields are selected, so a CAD record fills nothing. This is a valid setting and the apply dialog will offer nothing.'
                  : 'A field not selected here is never written from a CAD record, whatever a request asks for.'}
                {partialFieldGroups.size > 0 &&
                  ' One group is only partly allowed, shown as a dash rather than a tick. Select it to allow the whole group, which is how it is meant to be stored.'}
              </Typography>
            </FormControl>

            <Divider />

            {/* ---- Scoring ---- */}
            <Typography variant="subtitle1" component="h3">
              Scoring against the CAD record
            </Typography>

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.autoRescore}
                    onChange={(e) => set('autoRescore', e.target.checked)}
                    inputProps={{ 'aria-describedby': 'cad-rescore-help' }}
                  />
                }
                label="Score the categories below automatically"
              />
              <Typography id="cad-rescore-help" variant="body2" color="text.secondary">
                {scoringSentence} With this off, a supervisor asks for it from the CAD Record tab or
                the assistant, and a regeneration still re-applies a score that was asked for once.
              </Typography>
            </Box>

            <Autocomplete
              multiple
              // Stale values are included as options so Autocomplete does not log "none of the
              // options match" on every render of a tenant whose rubric has moved on.
              options={[...rubricCategories, ...staleCategories]}
              value={selectedCategories}
              onChange={(_e, value) => set('rescoreCategories', value.join(','))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Rubric categories the CAD record may score"
                  helperText="Only categories your analysis rubric defines can be chosen. Leave empty for a CAD record that scores nothing. Listing a category such as Case Entry means caller name and callback number are scored against a document that can carry values the caller never said."
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  const known = rubricCategories.includes(option);
                  return (
                    <Chip
                      key={key}
                      label={known ? option : `${option} (not in the rubric)`}
                      // warning.main behind contrastText is 3.71:1, below the 4.5:1 this 0.8125rem
                      // label needs. warning.dark clears it. The meaning is in the label either way.
                      sx={
                        known
                          ? undefined
                          : { bgcolor: 'warning.dark', color: 'warning.contrastText' }
                      }
                      {...tagProps}
                    />
                  );
                })
              }
            />

            {rubricCategories.length === 0 && (
              <Alert severity="warning">
                Your analysis rubric defines no scoring categories, so nothing can be chosen here.
                Set the rubric on the AI Settings tab first.
              </Alert>
            )}

            {staleCategories.length > 0 && (
              <Alert severity="warning">
                <AlertTitle>
                  {staleCategories.length === 1
                    ? 'One chosen category is not in the analysis rubric'
                    : 'Some chosen categories are not in the analysis rubric'}
                </AlertTitle>
                {sentence(staleCategories)} {staleCategories.length === 1 ? 'is' : 'are'} listed
                here but the rubric does not define {staleCategories.length === 1 ? 'it' : 'them'},
                so {staleCategories.length === 1 ? 'it scores' : 'they score'} nothing. Remove{' '}
                {staleCategories.length === 1 ? 'it' : 'them'}, or add the category to the analysis
                prompt.
              </Alert>
            )}

            <TextField
              label="Scoring instructions"
              value={settings.rescorePrompt}
              onChange={(e) => set('rescorePrompt', e.target.value)}
              multiline
              minRows={4}
              helperText="How the CAD record is used as evidence when scoring. Leave empty to use the platform default. The rubric and the format of the answer are added automatically."
            />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || reloading}
                aria-busy={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="outlined" onClick={handleReset} disabled={saving || reloading}>
                {reloading ? 'Reloading…' : 'Reset'}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
